import { ProfileCreateRequest, ProfileCreateUpdateResponse, ProfileResponse, ProfileUpdateRequest, ProfileConfigurationResponse, ProfileMaterialConfigurationRequest } from "@api/apiClient";
import { useCreateProfile, useDeleteProfile, useProfileDetails, useProfiles, useUpdateProfile } from "@api/profiles";
import { useCo2MaterialTypes } from "@api/co2MaterialTypes";
import DataGrid from "@components/DataGrid/DataGrid";
import MaterialSelectDialog from "@components/MaterialSelectDialog/MaterialSelectDialog";
import {  getDetailPanelContent, ProfileResponseModel } from "./ProfileDetailPanel";
import { GridToolbarImportButton } from "@components/DataGrid/components/toolbar/GridToolbarImportButton";
import MultiGridToolbarSearchFilter from "@components/DataGrid/components/toolbar/MultiGridToolbarSearchFilter";
import ProfileImportDisplay from "./ProfileImportDisplay";
import i18n from "@i18n/i18n";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import SettingsIcon from "@mui/icons-material/Settings";

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Switch, TextField, Tooltip, Alert } from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridSlotsComponentsProps,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { observer } from "mobx-react";
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import useResizeObserver from "use-resize-observer";
import { useNodeHierarchy } from "@api/nodehierarchy";
import { useSaveProfileMaterialConfiguration, useProfileMaterialConfiguration } from "@api/profileMaterialConfiguration";
import { transformTreeDataToJSON, transformJSONToTreeData, populateMaterialNames } from "@utils/materialConfigurationTransform";

// Custom TabPanel component
function CustomTabPanel(props: { children?: React.ReactNode; value: number; index: number }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`profile-detail-tabpanel-${index}`} aria-labelledby={`profile-detail-tab-${index}`}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

// Tree data utility function
function buildTreePath(nodes: any[]) {
  const map = new Map(nodes.map((n: any) => [n.id, n]));

  return nodes.map((node: any) => {
    const path = [];
    let current = node;

    while (current) {
      path.unshift(current.name);
      current = map.get(current.parentId);
    }

    return {
      ...node,
      path
    };
  });
}
// Simple MUI DataGrid Component using Node Hierarchy data - Now accepts profileId prop
const SimpleTreeGrid = forwardRef(({ profileId }: { profileId?: number }, ref) => {
  const { data: nodeHierarchyData, isLoading, error } = useNodeHierarchy();
  const { data: existingConfiguration, isLoading: configLoading } = useProfileMaterialConfiguration(profileId || 0, { enabled: !!profileId });
  const co2MaterialTypes = useCo2MaterialTypes();
  const [treeData, setTreeData] = useState<any[]>([]);
  const [openMaterialDlg, setOpenMaterialDlg] = useState(false);
  const [editCellId, setEditCellId] = useState<any>(null);
  const [editingMaterialField, setEditingMaterialField] = useState<string>(''); // Track which material field is being edited
  const materialDlgRef = useRef<any>(null);
  
  // Expose getTreeData method to parent component
  useImperativeHandle(ref, () => ({
    getTreeData: () => treeData
  }));
  
  console.info("Node Hierarchy Data:", nodeHierarchyData);
  console.info("Profile ID for tree grid:", profileId);
  
  // Function to mark all parent nodes as having child changes for specific fields
  const markParentsWithChildChanges = (childId: any, changedField: string, data: any[]) => {
    const updatedData = [...data];
    const child = updatedData.find(item => item.id === childId);
    
    if (child && child.parentId !== null && child.parentId !== undefined) {
      const markParent = (parentId: any) => {
        const parentIndex = updatedData.findIndex(item => item.id === parentId);
        if (parentIndex !== -1) {
          const changedFields = updatedData[parentIndex].changedFields || [];
          if (!changedFields.includes(changedField)) {
            updatedData[parentIndex] = {
              ...updatedData[parentIndex],
              changedFields: [...changedFields, changedField]
            };
          }
          
          // Recursively mark grandparents too
          if (updatedData[parentIndex].parentId !== null && updatedData[parentIndex].parentId !== undefined) {
            markParent(updatedData[parentIndex].parentId);
          }
        }
      };
      
      markParent(child.parentId);
    }
    
    return updatedData;
  };

  // Function to cascade parent values to all children
  const cascadeToChildren = (parentId: any, field: string, value: any, data: any[]) => {
    const updatedData = [...data];
    const updateChildren = (currentParentId: any) => {
      updatedData.forEach((item, index) => {
        if (item.parentId === currentParentId) {
          updatedData[index] = { ...item, [field]: value };
          // Recursively update children of this child
          updateChildren(item.id);
        }
      });
    };
    updateChildren(parentId);
    return updatedData;
  };

  // Handle material selection
  const handleSelectMaterial = (selectedMaterialType: any = null) => {
    setOpenMaterialDlg(false);
    
    if (selectedMaterialType && editCellId && editingMaterialField) {
      let updatedData = [...treeData];
      const rowIndex = updatedData.findIndex(row => row.id === editCellId);
      
      if (rowIndex !== -1) {
        const materialIdField = editingMaterialField;
        const materialNameField = editingMaterialField.replace('Id', 'Name');
        
        const updatedRow = {
          ...updatedData[rowIndex],
          [materialIdField]: selectedMaterialType.co2MaterialTypeId,
          [materialNameField]: selectedMaterialType.name
        };
        
        updatedData[rowIndex] = updatedRow;
        
        // Cascade material to all children
        updatedData = cascadeToChildren(editCellId, materialIdField, selectedMaterialType.co2MaterialTypeId, updatedData);
        updatedData = cascadeToChildren(editCellId, materialNameField, selectedMaterialType.name, updatedData);
        
        // Mark parents with star if this is a child node
        updatedData = markParentsWithChildChanges(editCellId, materialIdField, updatedData);
        
        setTreeData(updatedData);
      }
    }
    
    setEditCellId(null);
    setEditingMaterialField('');
  };

  // Handle cell value changes
  const processRowUpdate = (newRow: any, oldRow: any) => {
    console.log('Row update:', { newRow, oldRow });
    
    let updatedData = [...treeData];
    
    // Update the current row
    const rowIndex = updatedData.findIndex(row => row.id === newRow.id);
    if (rowIndex !== -1) {
      updatedData[rowIndex] = newRow;
      
      // Check if rebar percentage fields were changed
      if (oldRow.rebarPercentage !== newRow.rebarPercentage) {
        updatedData = cascadeToChildren(newRow.id, 'rebarPercentage', newRow.rebarPercentage, updatedData);
        updatedData = markParentsWithChildChanges(newRow.id, 'rebarPercentage', updatedData);
      }
      if (oldRow.nonModelledPercentage !== newRow.nonModelledPercentage) {
        updatedData = cascadeToChildren(newRow.id, 'nonModelledPercentage', newRow.nonModelledPercentage, updatedData);
        updatedData = markParentsWithChildChanges(newRow.id, 'nonModelledPercentage', updatedData);
      }
      
      // Check if material fields were changed
      if (oldRow.rebarMaterialId !== newRow.rebarMaterialId) {
        updatedData = cascadeToChildren(newRow.id, 'rebarMaterialId', newRow.rebarMaterialId, updatedData);
        updatedData = markParentsWithChildChanges(newRow.id, 'rebarMaterialId', updatedData);
      }
      if (oldRow.rebarMaterialName !== newRow.rebarMaterialName) {
        updatedData = cascadeToChildren(newRow.id, 'rebarMaterialName', newRow.rebarMaterialName, updatedData);
      }
      
      // Check if non-modelled material fields were changed
      if (oldRow.nonModelledMaterialId !== newRow.nonModelledMaterialId) {
        updatedData = cascadeToChildren(newRow.id, 'nonModelledMaterialId', newRow.nonModelledMaterialId, updatedData);
        updatedData = markParentsWithChildChanges(newRow.id, 'nonModelledMaterialId', updatedData);
      }
      if (oldRow.nonModelledMaterialName !== newRow.nonModelledMaterialName) {
        updatedData = cascadeToChildren(newRow.id, 'nonModelledMaterialName', newRow.nonModelledMaterialName, updatedData);
      }
      
      // Check if auto-assign material fields were changed
      if (oldRow.autoAssignMaterialId !== newRow.autoAssignMaterialId) {
        updatedData = cascadeToChildren(newRow.id, 'autoAssignMaterialId', newRow.autoAssignMaterialId, updatedData);
        updatedData = markParentsWithChildChanges(newRow.id, 'autoAssignMaterialId', updatedData);
      }
      if (oldRow.autoAssignMaterialName !== newRow.autoAssignMaterialName) {
        updatedData = cascadeToChildren(newRow.id, 'autoAssignMaterialName', newRow.autoAssignMaterialName, updatedData);
      }
    }
    
    setTreeData(updatedData);
    return newRow;
  };

  const columns = [
    { field: 'name', 
      headerName: 'Name', 
      flex: 1,
      width: 180,
      editable: true
    },
    { 
      field: 'rebarPercentage', 
      headerName: 'Rebar Percentage', 
      width: 180,
      editable: true,
      renderCell: (params: any) => {
        const value = params.value || '';
        const hasChanges = params.row?.changedFields?.includes('rebarPercentage');
        return (
          <span>
            {value}
            {hasChanges && <span style={{ color: 'orange', marginLeft: '5px' }}>★</span>}
          </span>
        );
      }
    },
    { 
      field: 'nonModelledPercentage', 
      headerName: 'Non Modelled Percentage', 
      width: 180,
      editable: true,
      renderCell: (params: any) => {
        const value = params.value || '';
        const hasChanges = params.row?.changedFields?.includes('nonModelledPercentage');
        return (
          <span>
            {value}
            {hasChanges && <span style={{ color: 'orange', marginLeft: '5px' }}>★</span>}
          </span>
        );
      }
    },
    {
      field: 'rebarMaterialId',
      headerName: 'Rebar Materials',
      editable: true,
      flex: 1,
      renderCell: (params: any) => {
        let displayValue = '';
        if (co2MaterialTypes.data?.length > 0) {
          const co2MaterialType = co2MaterialTypes.data?.find((type: any) => type.co2MaterialTypeId === params.row?.rebarMaterialId);
          displayValue = co2MaterialType?.name || '';
        } else {
          displayValue = params.row?.rebarMaterialName || '';
        }
        
        const hasChanges = params.row?.changedFields?.includes('rebarMaterialId');
        return (
          <span>
            {displayValue}
            {hasChanges && <span style={{ color: 'orange', marginLeft: '5px' }}>★</span>}
          </span>
        );
      }
    },
    {
      field: 'nonModelledMaterialId',
      headerName: 'Non Modelled Material',
      editable: true,
      flex: 1,
      renderCell: (params: any) => {
        let displayValue = '';
        if (co2MaterialTypes.data?.length > 0) {
          const co2MaterialType = co2MaterialTypes.data?.find((type: any) => type.co2MaterialTypeId === params.row?.nonModelledMaterialId);
          displayValue = co2MaterialType?.name || '';
        } else {
          displayValue = params.row?.nonModelledMaterialName || '';
        }
        
        const hasChanges = params.row?.changedFields?.includes('nonModelledMaterialId');
        return (
          <span>
            {displayValue}
            {hasChanges && <span style={{ color: 'orange', marginLeft: '5px' }}>★</span>}
          </span>
        );
      }
    },
    {
      field: 'autoAssignMaterialId',
      headerName: 'Auto Assign Material',
      editable: true,
      flex: 1,
      renderCell: (params: any) => {
        let displayValue = '';
        if (co2MaterialTypes.data?.length > 0) {
          const co2MaterialType = co2MaterialTypes.data?.find((type: any) => type.co2MaterialTypeId === params.row?.autoAssignMaterialId);
          displayValue = co2MaterialType?.name || '';
        } else {
          displayValue = params.row?.autoAssignMaterialName || '';
        }
        
        const hasChanges = params.row?.changedFields?.includes('autoAssignMaterialId');
        return (
          <span>
            {displayValue}
            {hasChanges && <span style={{ color: 'orange', marginLeft: '5px' }}>★</span>}
          </span>
        );
      }
    }
  ];
  
  console.info("Node Hierarchy Data:", nodeHierarchyData);
  
  // Initialize tree data when hierarchy data changes
  useEffect(() => {
    if (nodeHierarchyData) {
      let transformedData;
      
      if (existingConfiguration && existingConfiguration.length > 0) {
        console.info("Existing Configuration Data:", existingConfiguration);
        // Transform existing configuration to tree data format
        const configRequest = new ProfileMaterialConfigurationRequest();
        configRequest.profileId = profileId?.toString() || '';
        configRequest.nodeConfigurations = existingConfiguration;
        transformedData = transformJSONToTreeData(configRequest, nodeHierarchyData);
        
        // Populate material names using CO2 material types
        if (co2MaterialTypes.data?.length > 0) {
          transformedData = populateMaterialNames(transformedData, co2MaterialTypes.data);
        }
      } else {
        // Use default empty configuration
        transformedData = Array.from(
          new Map(
            nodeHierarchyData.map((nodeHierarchy, index) => [
              nodeHierarchy.name || 'Unnamed Profile',
              {
                id: nodeHierarchy.id || index + 1,
                name: nodeHierarchy.name || 'Unnamed Profile',
                nodeType: nodeHierarchy.nodeType || 'Profile',
                parentId: nodeHierarchy.parentId,
                rebarPercentage: 0, // Default value for Rebar Percentage
                nonModelledPercentage: 0, // Default value for Non-modelled Percentage
                rebarMaterialId: null, // Default rebar material ID
                rebarMaterialName: null, // Default rebar material name
                nonModelledMaterialId: null, // Default non-modelled material ID
                nonModelledMaterialName: null, // Default non-modelled material name
                autoAssignMaterialId: null, // Default auto-assign material ID
                autoAssignMaterialName: null, // Default auto-assign material name
                changedFields: [] // Track which specific fields have been modified in children
              }
            ])
          ).values()
        );
      }
      
      console.info("Transformed Node Hierarchy Data:", transformedData);
      setTreeData(transformedData);
    }
  }, [ existingConfiguration,nodeHierarchyData]);
  
  const data = buildTreePath(treeData);

  if (isLoading) {
    return (
      <Box sx={{ height: 600, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>Loading node hierarchy data...</p>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ height: 600, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>Error loading node hierarchy data: {error.message}</p>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <DataGrid
        rows={data}
        columns={columns}
        getRowId={(row) => row.id}
        treeData
        getTreeDataPath={(row : any) => row.path}
        editMode="cell"
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => console.error('ProcessRowUpdate Error:', error)}
        onCellEditStart={(params: any, _event: any) => {
          if (params.field === "rebarMaterialId" || params.field === "nonModelledMaterialId" || params.field === "autoAssignMaterialId") {
            setEditCellId(params.id);
            setEditingMaterialField(params.field);
            setOpenMaterialDlg(true);
          }
        }}
      />
      {openMaterialDlg && (
        <MaterialSelectDialog
          ref={materialDlgRef}
          open={openMaterialDlg}
          onClose={() => handleSelectMaterial()}
          onSubmit={(selectedMaterialType: any) => handleSelectMaterial(selectedMaterialType)}
        />
      )}
    </Box>
  );
});

// Common grid columns for all tabs

const ProfileGrid = observer(() => {
  const [addDisabled, setAddDisabled] = useState(false);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [rows, setRows] = useState<ProfileResponseModel[]>([]);
  const [isImportMode, setIsImportMode] = useState(false);
  const [searchText, setSearchText] = useState("");
  const getProfiles = useProfileDetails();
  const apiRef = useGridApiRef();

  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });
  const [coordinates, setCoordinates] = useState({ rowIndex: 0, colIndex: 0 });
  const [openSnackToast, setOpenSnackToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState<boolean>(false);
  const [profileName, setProfileName] = useState<string>("");
  const [profileDescription, setProfileDescription] = useState<string>("");
  const saveProfileMaterialConfiguration = useSaveProfileMaterialConfiguration();
  // Material configuration dialog state
  const [openMaterialConfigDialog, setOpenMaterialConfigDialog] = useState<boolean>(false);
  const [selectedProfile, setSelectedProfile] = useState<ProfileResponseModel | null>(null);
  const treeGridRef = useRef<{ getTreeData: () => any[] }>(null);

  const onError = (data: ProfileCreateUpdateResponse) => {
    if (data?.isDuplicate) {
      setOpenSnackToast(true);
      setToastMessage(i18n.t("profile-tab.notification.duplicate-profile-name"));
    }
  };

  const createProfiles = useCreateProfile({ onError });
  const updateProfiles = useUpdateProfile({ onError });
  const deleteProfiles = useDeleteProfile();

  // Handle opening material configuration dialog
  const handleMaterialConfigClick = (profile: ProfileResponseModel) => {
    setSelectedProfile(profile);
    setOpenMaterialConfigDialog(true);
  };

  // Handle closing material configuration dialog
  const handleCloseMaterialConfigDialog = () => {
    setOpenMaterialConfigDialog(false);
    setSelectedProfile(null);
  };

  // Handle saving material configuration
  const handleSaveMaterialConfig = async () => {
    try {
      // Get grid data from SimpleTreeGrid
      const gridData = treeGridRef.current?.getTreeData();
      console.info('Grid data from SimpleTreeGrid:', gridData);
      
      // Transform grid data to API format
      let nodeConfigurations: any[] = [];
      if (gridData && gridData.length > 0) {
        const transformedData = transformTreeDataToJSON(gridData, selectedProfile?.profileId?.toString() || '');
        nodeConfigurations = transformedData.nodeConfigurations || [];
      }
      
      const data = new ProfileMaterialConfigurationRequest({
        profileId: selectedProfile?.profileId,
        nodeConfigurations: nodeConfigurations,
      });
      console.info("Saving material configuration data:", data);
      
      await saveProfileMaterialConfiguration.mutateAsync(data);
      
      // Show success message
      setSuccessSnackbarOpen(true);
      
      // Close the dialog
      handleCloseMaterialConfigDialog();
    } catch (error) {
      console.error("Error saving material configuration:", error);
      setToastMessage("Error saving configuration");
      setOpenSnackToast(true);
    }
  };

  // useEffect(() => {
  //   apiRef.current.scrollToIndexes(coordinates);
  //   apiRef.current.setCellFocus(coordinates.rowIndex, "name");
  // }, [apiRef, coordinates]);

  useEffect(() => {
    if (getProfiles.data) {
      const model: ProfileResponseModel[] = [];
      getProfiles.data.forEach((profile: ProfileResponse, index: number) => {
        console.log("Raw profile", profile);
        model.push({
          srNo: index + 1,
          profileId: profile.profileId,
          name: profile.name,
          description: profile.description,
          deleteStatus: profile.deleteStatus,
          isDeleted: profile.isDeleted,
          rebarEnabled: profile.rebarEnabled,
          ProfileConfigurationResponse: profile.profileConfigurationResponse,
        } as any);
      });

      setRows(model);
    }
  }, [getProfiles.data]);

  // Apply search filter to grid
  // useEffect(() => {
  //   const quickFilterValues = searchText.split(" ").filter((word) => word !== "");
  //   apiRef.current.setQuickFilterValues(quickFilterValues);
  // }, [searchText]);

  const columns: GridColDef[] = [
    {
      field: "srNo",
      headerName: i18n.t("profile-tab.grid.columns.id"),
      editable: false,
      flex: 1,
    },
    {
      field: "name",
      headerName: i18n.t("profile-tab.grid.columns.name"),
      editable: true,
      flex: 1,
    },
    {
      field: "description",
      headerName: i18n.t("profile-tab.grid.columns.description"),
      editable: true,
      flex: 1,
    },
    {
      field: "rebarEnabled",
      headerName: i18n.t("profile-tab.grid.columns.rebar-enabled"),
      type: "boolean",
      editable: true,
      flex: 1,
      renderCell: (params) => {
        console.log("renderCell value for rebarEnabled:", params.value);
        return params.value ? "✅ Enabled" : "❌ Disabled";
      },
      renderEditCell: (params) => {
        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          event.preventDefault();
          console.log("Rebar enabled switch changed:", event.target.checked);
          const value = event.target.checked;
          // Just update the grid's edit model, don't call the API yet
          params.api.setEditCellValue({ id: params.id, field: params.field, value }, event);
        };

        return <Switch checked={Boolean(params.value)} onChange={handleChange} color="primary" inputProps={{ "aria-label": "Rebar enabled switch" }} />;
      },
    },

    {
      field: "actions",
      type: "actions",
      headerName: i18n.t("profile-tab.grid.columns.actions"),
      resizable: false,
      width: 150,
      getActions: ({ id, row }) => {
        // do not allow to edit for default row
        if (row.name == "Default") return [];

        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={`save-${id}`}
              icon={
                <Tooltip title={i18n.t("profile-tab.grid.save-button.tooltip")}>
                  <SaveIcon />
                </Tooltip>
              }
              label={i18n.t("profile-tab.grid.save-button.label")}
              onClick={() => handleSaveClick(id)}
              sx={{
                color: "primary.main",
              }}
            />,
            <GridActionsCellItem
              key={`cancel-${id}`}
              icon={
                <Tooltip title={i18n.t("profile-tab.grid.cancel-button.tooltip")}>
                  <CancelIcon />
                </Tooltip>
              }
              label={i18n.t("profile-tab.grid.cancel-button.label")}
              onClick={() => handleCancelClick(id)}
            />,
          ];
        } else {
          return [
            <GridActionsCellItem
              key={`configure-${id}`}
              icon={
                <Tooltip title="Configure Materials">
                  <SettingsIcon />
                </Tooltip>
              }
              label="Configure"
              onClick={() => handleMaterialConfigClick(row)}
            />,
            <GridActionsCellItem
              key={`edit-${id}`}
              icon={
                <Tooltip title={i18n.t("profile-tab.grid.edit-button.tooltip")}>
                  <EditIcon />
                </Tooltip>
              }
              label={i18n.t("profile-tab.grid.edit-button.label")}
              onClick={() => handleEditClick(id)}
            />,
            <GridActionsCellItem
              key={`delete-${id}`}
              icon={
                <Tooltip title={i18n.t("profile-tab.grid.delete-button.tooltip")}>
                  <DeleteIcon />
                </Tooltip>
              }
              label={i18n.t("profile-tab.grid.delete-button.label")}
              onClick={() => handleDeleteClick(id, row)}
            />,
          ];
        }
      },
    },
  ];

  const handleAddRow = () => {
    const profileItem = {
      srNo: -1,
      name: "name",
      description: "description",
      rebarEnabled: false,
    } as ProfileResponseModel;

    setRows((oldRows: any) => [{ ...profileItem, isNew: true }, ...oldRows]);
    setAddDisabled(true);
    setTimeout(() => {
      apiRef.current.startRowEditMode({ id: -1 });
      setTimeout(() => {
        setCoordinates({ rowIndex: 0, colIndex: 0 });
      }, 1000);
    });
  };

  const handleEditClick = async (id: GridRowId) => {
    console.log("Edit clicked: ", id);
    apiRef.current.startRowEditMode({ id });
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleDeleteClick = async (id: GridRowId, row: any) => {
    console.log(id);
    if (!row?.profileId) {
      throw new Error("Unable to delete.");
    }

    await deleteProfiles.mutateAsync(row.profileId);
    getProfiles.refetch();
  };

  const handleSaveClick = async (id: GridRowId) => {
    setAddDisabled(false);
    apiRef.current.stopRowEditMode({
      id,
      ignoreModifications: false,
    });
    setRowModesModel((prevModes) => {
      const updatedModes = { ...prevModes, [id]: { mode: GridRowModes.View } };
      return updatedModes;
    });
  };

  const handleCancelClick = (id: GridRowId) => {
    console.log("Cancel clicked: ", id);
    setAddDisabled(false);
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View, ignoreModifications: true } });

    const newRows = rows.filter((r: any) => r.isNew !== true);
    setRows(newRows);
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    console.log("handleRowModesModelChange: ", newRowModesModel);
    setRowModesModel(newRowModesModel);
  };

  const handleCellEditStart: GridEventListener<"cellEditStart"> = (params, _event) => {
    console.log("handleCellEditStart: ", params);
    setAddDisabled(true);
  };

  const handleCellEditStop: GridEventListener<"cellEditStop"> = (params, _event) => {
    console.log("handleCellEditStop: ", params);
    setAddDisabled(false);
  };

  const handleRowEditStart: GridEventListener<"rowEditStart"> = (params, _event) => {
    console.log("handleRowEditStart: ", params);
    setAddDisabled(true);
  };

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (params, event) => {
    console.log("handleRowEditStop: ", params);

    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }

    setAddDisabled(rows.some((r: any) => r.isNew === true));
  };

  const processRowUpdate = async (newRow: GridRowModel, oldRow: GridRowModel) => {
    console.log(oldRow);
    console.log("🚀 processRowUpdate triggered");
    console.log("🔍 newRow:", newRow);
    if (newRow.ignoreModifications) {
      return newRow;
    }

    let response: ProfileCreateUpdateResponse;
    if (newRow.srNo === -1) {
      response = await createProfiles.mutateAsync(
        new ProfileCreateRequest({
          name: newRow.name,
          description: newRow.description,
          rebarEnabled: newRow.rebarEnabled,
        }) as any,
      );
    } else {
      response = await updateProfiles.mutateAsync(
        new ProfileUpdateRequest({
          profileId: newRow.profileId,
          name: newRow.name,
          description: newRow.description,
          rebarEnabled: newRow.rebarEnabled,
        }) as any,
      );
      console.log("🚀 response:", response);
    }
    if (response.isDuplicate) {
      setOpenSnackToast(true);
      setToastMessage(i18n.t("profile-tab.notification.duplicate-profile-name"));
    } else {
      getProfiles.refetch();
      return newRow;
    }
  };

  const [importFiles, setImportFiles] = useState<File[] | undefined>(undefined);

  const handleFileImport = async (files: File[]) => {
    // Store the original files array immediately to prevent garbage collection
    const filesCopy = [...files];
    setImportFiles(filesCopy);
    setIsImportMode(true);
  };

  const handleImportComplete = () => {
    setIsImportMode(false);
    getProfiles.refetch();
  };

  const handleImportCancel = () => {
    setIsImportMode(false);
    setImportFiles(undefined);
  };

  const handleOpenDialog = () => {
    setProfileName("");
    setProfileDescription("");
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setProfileName("");
    setProfileDescription("");
  };

  const handleCreateProfile = async () => {
    if (profileName.trim()) {
      const profileItem = {
        srNo: -1,
        name: profileName.trim(),
        description: profileDescription.trim(),
        rebarEnabled: false,
      } as ProfileResponseModel;

      setRows((oldRows: any) => [{ ...profileItem, isNew: true }, ...oldRows]);
      setAddDisabled(true);
      handleCloseDialog();
      
      setTimeout(() => {
        apiRef.current.startRowEditMode({ id: -1 });
        setTimeout(() => {
          setCoordinates({ rowIndex: 0, colIndex: 0 });
        }, 1000);
      });
    }
  };

  return (
    <Box ref={containerRef} sx={{ width: "100%", height: "100%" }}>
      {isImportMode ? (
        <ProfileImportDisplay files={importFiles} onImportComplete={handleImportComplete} onCancel={handleImportCancel} />
      ) : (
        <>
          
          <DataGrid
            apiRef={apiRef}
            loading={getProfiles.isFetching}
            columns={columns}
            rows={rows}
            getRowId={(row) => row?.srNo}
            hideFooter={false}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onCellEditStart={handleCellEditStart}
            onCellEditStop={handleCellEditStop}
            onRowEditStart={handleRowEditStart}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(error) => console.error("onProcessRowUpdateError: ", error)}
            rowExpander
            getDetailPanelContent={getDetailPanelContent}
            slots={{ toolbar: EditToolbar }}
            slotProps={{
              toolbar: {
                onAddRow: handleOpenDialog,
                //onOpenDialog: handleOpenDialog,
                onFileImport: handleFileImport,
                disabled: addDisabled,
                searchText,
                onSearchTextChange: (text: string | undefined) => setSearchText(text || ""),
              },
            }}
            sx={{ height: `${(height || 400) - 16}px` }}
          />

          {/* Material Configuration Dialog */}
          <Dialog 
            open={openMaterialConfigDialog} 
            onClose={handleCloseMaterialConfigDialog} 
            maxWidth="xl" 
            fullWidth
            PaperProps={{
              sx: { height: '90vh' }
            }}
          >
            <DialogTitle>
              Material Configuration - {selectedProfile?.name}
            </DialogTitle>
            <DialogContent>
              {selectedProfile && (
                <SimpleTreeGrid 
                  ref={treeGridRef}
                  profileId={selectedProfile.profileId as unknown as number} 
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseMaterialConfigDialog}>Cancel</Button>
              <Button onClick={handleSaveMaterialConfig} variant="contained">
                Save Configuration
              </Button>
            </DialogActions>
          </Dialog>

          {openSnackToast && (
            <Snackbar
              open={openSnackToast}
              autoHideDuration={2000}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              message={toastMessage}
              onClose={() => setOpenSnackToast(false)}
            />
          )}

          {/* Success Snackbar */}
          <Snackbar
            open={successSnackbarOpen}
            autoHideDuration={3000}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            onClose={() => setSuccessSnackbarOpen(false)}
            sx={{
              '& .MuiSnackbarContent-root': {
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                position: 'fixed',
              }
            }}
          >
            <Alert 
              onClose={() => setSuccessSnackbarOpen(false)} 
              severity="success" 
              sx={{ width: '100%' }}
            >
              Material configuration saved successfully!
            </Alert>
          </Snackbar>

          <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>Create New Profile</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Profile Name"
                type="text"
                fullWidth
                variant="outlined"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="Description"
                type="text"
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                value={profileDescription}
                onChange={(e) => setProfileDescription(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button onClick={handleCreateProfile} variant="contained" disabled={!profileName.trim()}>
                Create
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
});

const EditToolbar = (
  props: NonNullable<GridSlotsComponentsProps["toolbar"]> & { onFileImport?: (files: File[]) => Promise<void>; onOpenDialog?: () => void; searchText?: string; onSearchTextChange?: (text: string) => void },
) => {
  return (
    <GridToolbarContainer>
      <Tooltip title={i18n.t("profile-tab.grid.add-button.tooltip")}>
        {/* <Button variant="outlined" onClick={() => props.onAddRow?.()} disabled={props.disabled}>
          {i18n.t("profile-tab.grid.add-button.label")}
        </Button> */}

         <Button variant="outlined" onClick={() => props.onAddRow?.()} disabled={props.disabled}>
          {i18n.t("profile-tab.grid.add-button.label")}
        </Button>
      </Tooltip>
      <MultiGridToolbarSearchFilter value={props.searchText} onChange={props.onSearchTextChange} />
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarImportButton onFileImport={props.onFileImport} />
    </GridToolbarContainer>
  );
};

export default ProfileGrid;
